/**
 * ForgotPassword — public page for self-service password reset initiation.
 *
 * Posts the email to `/api/auth/forgot-password` and shows the SAME generic
 * success message regardless of API response (errors included). This
 * prevents leaking which emails are registered.
 *
 * Issue: #320
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { tenantName, tenantLogo } from '../config/tenant';
import { forgotPassword } from '../services/authApi';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
    } catch {
      // Swallow — we always show the same success UI regardless of API result.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ems-bg">
      <div className="w-full max-w-xl px-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-12">
          <div className="text-center mb-10">
            <img
              src={tenantLogo}
              alt={tenantName}
              className="w-72 h-72 mx-auto mb-6 object-contain"
            />
          </div>

          {submitted ? (
            <div className="space-y-4 text-center">
              <MailCheck className="w-10 h-10 text-ems-green mx-auto" />
              <h2 className="text-lg font-semibold text-gray-900">Check your email</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                If an account exists for that email, we have sent a password reset
                link. It will be valid for the next 60 minutes.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-ems-green hover:underline"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Forgot your password?</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Enter your email and we will send you a reset link.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || email.length === 0}
                className="w-full py-3 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold text-base transition-colors hover:bg-ems-forest/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm text-ems-green hover:underline"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl border border-gray-200">
            <img
              src="/djin-logo.png"
              alt="DJIN Energy Management Systems"
              className="w-9 h-9 rounded-full object-contain"
            />
            <div>
              <p className="text-xs text-gray-500 font-medium">Powered by</p>
              <p className="text-sm font-bold text-gray-900">DJIN Energy Management Systems</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
